import { useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tag, X, Loader2 } from 'lucide-react'
import type { ApplicableDiscount, DiscountScope } from '@/types/pos'
import type { PaymentMethodType } from '@/types/payment-methods'
import { apiFetch } from '@/utils'

interface PromoCodeInputProps {
	/** Transaction scope for validation */
	transactionType: DiscountScope
	/** The subtotal to validate the discount against */
	subtotal: number
	/** Optional: membership template ID for targeted discounts */
	membershipTemplateId?: number
	/** Optional: guest type for targeted discounts */
	guestType?: string
	/** Optional: entry pass template ID for targeted discounts */
	entryPassTemplateId?: number
	/** Optional: day of week (0-6) for entry pass targeting */
	dayOfWeek?: number
	/** Optional: time slot for entry pass targeting */
	timeSlot?: string
	/** Optional: payment method type for payment-method-targeted discounts */
	paymentMethod?: PaymentMethodType
	/** Callback when a discount is applied or removed */
	onDiscountChange: (discount: ApplicableDiscount | null) => void
	/** Currently applied discount (for display) */
	appliedDiscount: ApplicableDiscount | null
	/** Callback when a discount is removed — parent can use this to re-trigger auto-apply */
	onRemoveDiscount?: () => void
}

export default function PromoCodeInput({
	transactionType,
	subtotal,
	membershipTemplateId,
	guestType,
	entryPassTemplateId,
	dayOfWeek,
	timeSlot,
	paymentMethod,
	onDiscountChange,
	appliedDiscount,
	onRemoveDiscount,
}: PromoCodeInputProps) {
	const [promoCode, setPromoCode] = useState('')
	const [isValidating, setIsValidating] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleApply = useCallback(async () => {
		const code = promoCode.trim().toUpperCase()
		if (!code) return

		setIsValidating(true)
		setError(null)

		try {
			const body: Record<string, any> = {
				items: [],
				locationId: 0,
				cartSubtotal: subtotal,
				promoCode: code,
				transactionType,
			}
			// Add optional targeting context
			if (membershipTemplateId) body.membershipTemplateId = membershipTemplateId
			if (guestType) body.guestType = guestType
			if (entryPassTemplateId) body.entryPassTemplateId = entryPassTemplateId
			if (dayOfWeek != null) body.dayOfWeek = dayOfWeek
			if (timeSlot) body.timeSlot = timeSlot
			if (paymentMethod) body.paymentMethod = paymentMethod

			const res = await apiFetch('/v1/pos/discounts/validate', {
				method: 'POST',
				withCredentials: true,
				body: JSON.stringify(body),
			})

			if (!res.ok) {
				const data = await res.json().catch(() => null)
				throw new Error(data?.message || 'Invalid promo code')
			}

			const data = await res.json()

			if (!data.bestDiscount) {
				throw new Error('This promo code is not applicable to your purchase')
			}

			const newDiscount = data.bestDiscount

			// Compare: only apply if better than current discount
			if (appliedDiscount && appliedDiscount.calculatedAmount >= newDiscount.calculatedAmount) {
				setError(`Your current discount saves you more ($${appliedDiscount.calculatedAmount.toFixed(2)} vs $${newDiscount.calculatedAmount.toFixed(2)})`)
				return
			}

			onDiscountChange(newDiscount)
			setPromoCode('')
		} catch (err: any) {
			setError(err?.message || 'Failed to validate promo code')
		} finally {
			setIsValidating(false)
		}
	}, [promoCode, transactionType, subtotal, membershipTemplateId, guestType, entryPassTemplateId, dayOfWeek, timeSlot, paymentMethod, onDiscountChange, appliedDiscount])

	const handleRemove = useCallback(() => {
		setPromoCode('')
		setError(null)
		if (onRemoveDiscount) {
			onRemoveDiscount()
		} else {
			onDiscountChange(null)
		}
	}, [onDiscountChange, onRemoveDiscount])

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			handleApply()
		}
	}, [handleApply])

	return (
		<div className="mt-3">
			{/* Applied Discount Badge — matches POS cart-modal styling */}
			{appliedDiscount && (
				<div className="p-3 rounded-lg bg-[#F0FDF4] border border-[#22C55E] mb-3">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Tag className="h-4 w-4 text-[#22C55E]" />
							<div>
								<p className="font-montserrat text-xs font-semibold text-[#166534]">
									{appliedDiscount.discountName}
								</p>
								<p className="text-xs text-[#15803D]">
									{appliedDiscount.discountType === 'percentage'
										? `${appliedDiscount.discountValue}% off`
										: `$${Number(appliedDiscount.discountValue).toFixed(2)} off`}
									{appliedDiscount.promoCode && ` (${appliedDiscount.promoCode})`}
								</p>
							</div>
						</div>
						{/* Only show X button for manually applied promos, not auto-apply discounts */}
						{appliedDiscount.isAutoApply === false && (
							<button
								onClick={handleRemove}
								className="h-6 w-6 rounded-full bg-[#DCF4E4] flex items-center justify-center hover:bg-[#BBF7D0]"
							>
								<X className="h-3 w-3 text-[#166534]" />
							</button>
						)}
					</div>
				</div>
			)}

			{/* Promo Code Input — always visible so user can enter a different code */}
			<div className="p-3 rounded-lg bg-[#2F2F32]">
				<div className="flex gap-2">
					<div className="flex-1">
						<Input
							placeholder="Enter promo code"
							type="text"
							className="h-[38px] text-[13px] bg-[#18181B] border-[#4C4C4C] text-white placeholder:text-[#808080] uppercase w-full"
							value={promoCode}
							onChange={(e) => {
								setPromoCode(e.target.value.toUpperCase())
								setError(null)
							}}
							onKeyDown={handleKeyDown}
							disabled={isValidating}
						/>
						{error && (
							<p className="text-[11px] text-red-400 mt-1">{error}</p>
						)}
					</div>
					<Button
						type="button"
						className="h-[38px] px-4 text-[13px] bg-primary text-white hover:bg-primary/90"
						onClick={handleApply}
						disabled={isValidating || !promoCode.trim()}
					>
						{isValidating ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : (
							'Apply'
						)}
					</Button>
				</div>
			</div>
		</div>
	)
}
